import { Modal, ModalCloseButton } from '../Modal.js'
import { Button } from '../ui.js'
import type { ReportFinding, ReportFindingGroup } from './types.js'

/**
 * What is wrong with the report, before it goes.
 *
 * Nothing here refuses. The primary button reads "Complete anyway" when there
 * are findings, because a technician in a subfloor with one bar is the wrong
 * person to argue with about a blank field — and the findings are recorded
 * alongside the submission, so nobody can later claim the gaps were invisible.
 *
 * The three groups are in the order they matter. A contradiction is the one that
 * gets a report thrown out in an audit: each answer is defensible alone and the
 * pair is not.
 */

const GROUPS: Array<{
  group: ReportFindingGroup
  title: string
  tone: string
}> = [
  { group: 'CONTRADICTION', title: 'Do not add up', tone: 'bg-red-50 text-red-700 ring-red-200' },
  { group: 'VAGUE', title: 'Too vague for an audit', tone: 'bg-amber-50 text-amber-800 ring-amber-200' },
  { group: 'BLANK', title: 'Still blank', tone: 'bg-blue-50 text-blue-700 ring-blue-200' },
]

export function CheckSheet({
  open,
  onClose,
  findings,
  answered,
  total,
  submitting,
  onJump,
  onComplete,
}: {
  open: boolean
  onClose: () => void
  findings: ReportFinding[]
  answered: number
  total: number
  submitting: boolean
  onJump: (fieldKey: string) => void
  onComplete: () => void
}) {
  const clean = findings.length === 0

  return (
    <Modal open={open} onClose={onClose} labelledBy="check-sheet-title" className="max-w-[520px]">
      <div className="flex items-start gap-3 border-b border-ink-200 px-4 py-3.5">
        <div className="min-w-0 flex-1">
          <h2 id="check-sheet-title" className="text-[15px] font-semibold text-ink-900">
            Check before you send
          </h2>
          <p className="mt-0.5 text-xs text-ink-500">
            <span className="tabular-nums">{answered}</span> of{' '}
            <span className="tabular-nums">{total}</span> filled in
          </p>
        </div>
        <ModalCloseButton onClose={onClose} />
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
        {clean ? (
          <div className="rounded-xl bg-brand-50 px-3 py-5 text-center ring-1 ring-brand-200 ring-inset">
            <p className="text-sm font-semibold text-brand-900">Nothing outstanding.</p>
            <p className="mt-1 text-[13px] text-ink-600">
              Every field filled, nothing vague, nothing contradicting itself.
            </p>
          </div>
        ) : null}

        {GROUPS.map(({ group, title, tone }) => {
          const rows = findings.filter((finding) => finding.group === group)
          if (rows.length === 0) return null

          return (
            <section key={group} className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex h-[22px] items-center rounded-full px-2 text-[11px] font-semibold tabular-nums ring-1 ring-inset ${tone}`}
                >
                  {rows.length}
                </span>
                <h3 className="text-[13px] font-semibold text-ink-900">{title}</h3>
              </div>

              {rows.map((finding, index) => (
                <button
                  key={`${finding.fieldKey}-${index}`}
                  type="button"
                  onClick={() => onJump(finding.fieldKey)}
                  className="flex flex-col gap-0.5 rounded-xl bg-white px-3 py-2.5 text-left ring-1 ring-ink-200 ring-inset transition-colors hover:bg-ink-50"
                >
                  <span className="text-[13.5px] font-semibold text-ink-900">{finding.title}</span>
                  {finding.detail ? (
                    <span className="text-xs text-ink-500">{finding.detail}</span>
                  ) : null}
                </button>
              ))}
            </section>
          )
        })}
      </div>

      <div className="flex items-center gap-2.5 border-t border-ink-200 px-4 py-3">
        <Button onClick={onClose}>Keep filling in</Button>
        <Button
          variant="primary"
          size="lg"
          block
          loading={submitting}
          onClick={onComplete}
          className="rounded-full"
        >
          {clean ? 'Complete the report' : 'Complete anyway'}
        </Button>
      </div>
    </Modal>
  )
}
